import { idCard, mail, person, personCircleOutline, shieldCheckmark } from 'ionicons/icons';
import { useUserProfile } from '@/hooks/useUserProfile';
import { Card, CardContent, CardHeader, CardSubtitle } from '@/components/Card';
import { Icon } from '@/components/Icon';
import { PageSpinner } from '@/components/PageSpinner';
import { ErrorState } from '@/components/ErrorState';

/**
 * Port of user-profile.page.ts / user-profile.page.html.
 *
 * The Angular template also had an "Account status" card with two
 * checkmark-circle icons, commented out pending a customer-status feature. It
 * isn't carried over; add it here when that feature exists.
 */
export default function UserProfilePage() {
  // Read through the query object so isPending/isError narrow `data`.
  const profileQuery = useUserProfile();

  if (profileQuery.isPending) return <PageSpinner message="Loading your profile..." />;
  if (profileQuery.isError) {
    return (
      <ErrorState
        title="Could not load your profile"
        error={profileQuery.error}
        onRetry={() => void profileQuery.refetch()}
      />
    );
  }

  const data = profileQuery.data;

  const fields = [
    { label: 'User ID / email address', value: data.email, icon: mail },
    { label: 'Full name', value: data.name, icon: person },
    { label: 'Customer ID', value: data.customerId, icon: idCard },
  ];

  return (
    <div className="mx-auto max-w-2xl px-4 py-6">
      {/* The Angular .profile-header: an 80px orange avatar over the title. */}
      <header className="mb-8 text-center">
        <div className="mx-auto mb-4 flex size-20 items-center justify-center rounded-full bg-primary">
          <Icon icon={personCircleOutline} className="size-20 text-white" />
        </div>
        <h1 className="text-2xl font-semibold text-primary">User profile</h1>
      </header>

      <div className="space-y-4">
        {fields.map((field) => (
          <Card key={field.label}>
            <CardHeader>
              <CardSubtitle icon={field.icon}>{field.label}</CardSubtitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-900">{field.value || '—'}</p>
            </CardContent>
          </Card>
        ))}

        <Card>
          <CardHeader>
            <CardSubtitle icon={shieldCheckmark}>Role</CardSubtitle>
          </CardHeader>
          <CardContent>
            <span className="inline-flex rounded-full bg-secondary px-3 py-1 text-sm font-medium text-slate-900 capitalize">
              {data.role}
            </span>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
